import {
  WORKBOOK_FREEFORM_MAX_COLUMNS,
  WORKBOOK_FREEFORM_MAX_ROWS,
  type WorkbookCell,
} from '@erp/shared-types';
import {
  parseCellAddress,
  type CellPosition,
  type CellRange,
  type ColumnType,
} from '@erp/workbooks';

export type FreeformMove = 'up' | 'down' | 'left' | 'right';

/** The rows and columns shown: past every filled cell and the table, and at least a screenful. */
export function freeformGridSize(
  inputs: Readonly<Record<string, string>>,
  table: CellRange | null,
): { rows: number; columns: number } {
  let rows = table?.end.row ?? 0;
  let columns = table?.end.column ?? 0;
  for (const address of Object.keys(inputs)) {
    const position = parseCellAddress(address);
    if (!position) continue;
    rows = Math.max(rows, position.row);
    columns = Math.max(columns, position.column);
  }
  return {
    rows: Math.min(Math.max(rows + 5, 30), WORKBOOK_FREEFORM_MAX_ROWS),
    columns: Math.min(Math.max(columns + 2, 10), WORKBOOK_FREEFORM_MAX_COLUMNS),
  };
}

/** The cell one step from `from`, kept inside the grid. */
export function moveCell(
  from: CellPosition,
  move: FreeformMove,
  size: { rows: number; columns: number },
): CellPosition {
  const clamp = (value: number, max: number) =>
    Math.min(Math.max(value, 1), max);
  return {
    column: clamp(
      from.column + (move === 'right' ? 1 : move === 'left' ? -1 : 0),
      size.columns,
    ),
    row: clamp(
      from.row + (move === 'down' ? 1 : move === 'up' ? -1 : 0),
      size.rows,
    ),
  };
}

/** How a cell reads in the grid. */
export type FreeformShownCell =
  | { kind: 'empty' }
  /** A Formula the shown Result has no value for: its text, until a Run. */
  | { kind: 'formula'; text: string }
  | {
      kind: 'value';
      cell: WorkbookCell;
      type: ColumnType | undefined;
      /** It is the Result's, not what was typed. */
      computed: boolean;
    };

function typeOf(cell: WorkbookCell): ColumnType | undefined {
  if (typeof cell === 'number') return 'decimal';
  if (typeof cell === 'boolean') return 'boolean';
  return undefined;
}

const PLAIN_NUMBER = /^-?(0|[1-9][0-9]*)(\.[0-9]+)?$/;

/**
 * What a cell shows: a literal as typed (a leading ' marks text and is not
 * shown; a number reads as a Number), so an edit shows at once; a Formula's
 * computed value when the shown Result has one, else its text.
 */
export function freeformShownCell(
  input: string | undefined,
  value: WorkbookCell | undefined,
): FreeformShownCell {
  if (input === undefined || input === '') return { kind: 'empty' };
  if (!input.startsWith('='))
    return {
      kind: 'value',
      cell: input.replace(/^'/, ''),
      type: PLAIN_NUMBER.test(input) ? 'decimal' : undefined,
      computed: false,
    };
  if (value === undefined) return { kind: 'formula', text: input };
  return { kind: 'value', cell: value, type: typeOf(value), computed: true };
}

/** What a key pressed on the grid (not in a cell's editor) does. */
export type FreeformGridKeyAction =
  | { kind: 'move'; move: FreeformMove }
  /** Edit the selected cell: from what it holds, or replacing it with `text` as typed. */
  | { kind: 'edit'; text: string | null }
  | { kind: 'clear' };

const ARROWS: Readonly<Record<string, FreeformMove>> = {
  ArrowUp: 'up',
  ArrowDown: 'down',
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

/** A spreadsheet's grid keys: arrows and Tab move, Enter or F2 edits, Delete clears, a character starts typing. */
export function freeformGridKeyAction(event: {
  key: string;
  shiftKey: boolean;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
}): FreeformGridKeyAction | null {
  const arrow = ARROWS[event.key];
  if (arrow) return { kind: 'move', move: arrow };
  if (event.key === 'Tab')
    return { kind: 'move', move: event.shiftKey ? 'left' : 'right' };
  if (event.key === 'Enter' || event.key === 'F2')
    return { kind: 'edit', text: null };
  if (event.key === 'Delete' || event.key === 'Backspace')
    return { kind: 'clear' };
  const typing =
    event.key.length === 1 && !event.metaKey && !event.ctrlKey && !event.altKey;
  return typing ? { kind: 'edit', text: event.key } : null;
}
